import React, { useState } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { Search, ShoppingCart, User, Menu, X } from 'lucide-react';
import logo from '../../assets/homelogo.svg';

const Header = () => {
  const { currentUser, userProfile, logout } = useAuth();
  const { getCartCount } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Determine active category
  const isProductsPage = location.pathname === '/products';
  const currentCategory = searchParams.get('category') || '';
  const isHome = location.pathname === '/';
  const isSunglasses = isProductsPage && currentCategory === 'sunglasses';
  const isNearsighted = isProductsPage && currentCategory === 'nearsighted';
  const isAllProducts = isProductsPage && currentCategory === '';

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${searchQuery}`);
      setSearchQuery('');
    }
  };

  return (
    <header className="bg-white shadow-md sticky top-0 z-50">
      <div className="container mx-auto px-4">
        {/* Top Bar */}
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img src={logo} alt="Kính mắt Nhân Văn Logo" className="h-32 w-auto" />
          </Link>

          {/* Search Bar - Desktop */}
          <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-xl mx-8">
            <div className="relative w-full">
              <input
                type="text"
                placeholder="Tìm kiếm kính mát, kính cận..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <button type="submit" className="absolute right-3 top-1/2 -translate-y-1/2">
                <Search className="w-5 h-5 text-gray-400" />
              </button>
            </div>
          </form>

          {/* Right Icons */}
          <div className="flex items-center gap-4">
            {/* User Account */}
            <div className="relative group">
              <button className="flex items-center gap-2 hover:text-primary-600 transition">
                <User className="w-6 h-6" />
                <span className="hidden md:block text-sm">
                  {currentUser ? (userProfile?.displayName || currentUser.displayName || 'Tài khoản') : 'Đăng nhập'}
                </span>
              </button>

              {/* Dropdown */}
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                {currentUser ? (
                  <>
                    <Link to="/profile" className="block px-4 py-2 hover:bg-gray-100">
                      Hồ Sơ
                    </Link>
                    <Link to="/orders" className="block px-4 py-2 hover:bg-gray-100">
                      Đơn Hàng
                    </Link>
                    {userProfile?.role === 'admin' && (
                      <Link to="/admin/dashboard" className="block px-4 py-2 hover:bg-gray-100"> 
                        Quản lý
                      </Link>
                    )}
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 text-red-600"
                    >
                      Đăng Xuất
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/login" className="block px-4 py-2 hover:bg-gray-100">
                      Đăng Nhập
                    </Link>
                    <Link to="/register" className="block px-4 py-2 hover:bg-gray-100">
                      Đăng Ký
                    </Link>
                  </>
                )}
              </div>
            </div>

            {/* Shopping Cart */}
            <Link to="/cart" className="relative hover:text-primary-600 transition">
              <ShoppingCart className="w-6 h-6" />
              {getCartCount() > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {getCartCount()}
                </span>
              )}
            </Link>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="hidden md:block border-t py-3">
          <ul className="flex items-center justify-center gap-8">
            <li>
              <Link
                to="/"
                className={`hover:text-primary-600 transition font-medium ${
                  isHome ? 'text-primary-600 border-b-2 border-primary-600 pb-1' : ''
                }`}
              >
                Trang Chủ
              </Link>
            </li>
            <li>
              <Link
                to="/products?category=sunglasses"
                className={`hover:text-primary-600 transition font-medium ${
                  isSunglasses ? 'text-primary-600 border-b-2 border-primary-600 pb-1' : ''
                }`}
              >
                Kính Mát
              </Link>
            </li>
            <li>
              <Link
                to="/products?category=nearsighted"
                className={`hover:text-primary-600 transition font-medium ${
                  isNearsighted ? 'text-primary-600 border-b-2 border-primary-600 pb-1' : ''
                }`}
              >
                Kính Cận
              </Link>
            </li>
            <li>
              <Link
                to="/products"
                className={`hover:text-primary-600 transition font-medium ${
                  isAllProducts ? 'text-primary-600 border-b-2 border-primary-600 pb-1' : ''
                }`}
              >
                Tất Cả Sản Phẩm
              </Link>
            </li>
          </ul>
        </nav>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t py-4">
            {/* Mobile Search */}
            <form onSubmit={handleSearch} className="mb-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Tìm kiếm..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <button type="submit" className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Search className="w-5 h-5 text-gray-400" />
                </button>
              </div>
            </form>

            {/* Mobile Navigation */}
            <ul className="space-y-2">
              <li>
                <Link
                  to="/"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block py-2 hover:text-primary-600 transition ${
                    isHome ? 'text-primary-600 font-semibold' : ''
                  }`}
                >
                  Trang Chủ
                </Link>
              </li>
              <li>
                <Link
                  to="/products?category=sunglasses"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block py-2 hover:text-primary-600 transition ${
                    isSunglasses ? 'text-primary-600 font-semibold' : ''
                  }`}
                >
                  Kính Mát
                </Link>
              </li>
              <li>
                <Link
                  to="/products?category=nearsighted"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block py-2 hover:text-primary-600 transition ${
                    isNearsighted ? 'text-primary-600 font-semibold' : ''
                  }`}
                >
                  Kính Cận
                </Link>
              </li>
              <li>
                <Link
                  to="/products"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block py-2 hover:text-primary-600 transition ${
                    isAllProducts ? 'text-primary-600 font-semibold' : ''
                  }`}
                >
                  Tất Cả Sản Phẩm
                </Link>
              </li>
            </ul>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
